import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Button, CircularProgress, Box, IconButton } from "@mui/material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { Admin } from "../../../models/TaskTracker/Admin";
import { SortType } from "../../../models/SortType";
import TaskTable from "../../components/task-tracker/TaskTable";
import { FilterItem } from "../../../models/Filters/FilterValue";
import { useDebounce } from "../../components/common/hooks/Debounce";
import PageTitle from "../../components/common/pages/PageTitle";
import { status_list } from "../../../models/TaskTracker/TaskStatus";
import ChatSidebar from "../../components/task-tracker/ChatSidebar";
import CloseIcon from "@mui/icons-material/Close";
interface Filters {
  order_id: FilterItem[];
  owner_id: FilterItem[];
  reporter_id: FilterItem[];
  assignee_id: FilterItem[];
  status: FilterItem[];
}

const TaskTracker: React.FC = () => {
  const { TaskTrackerStore, chatStore, userStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  // const [taskId, setTaskId] = useState(0);

  const reporterSearchRef = useRef<string>("");
  const assigneeSearchRef = useRef<string>("");
  const orderIdSearchRef = useRef<string>("");

  const [, setSearchTrigger] = useState<number>(0);
  const location = useLocation();

  const debouncedReporterSearchText = useDebounce(
    reporterSearchRef.current,
    500
  );
  const debouncedAssigneeSearchText = useDebounce(
    assigneeSearchRef.current,
    500
  );
  const debouncedOrderIdSearchText = useDebounce(orderIdSearchRef.current, 500);

  const searchParams = new URLSearchParams(location.search);

  const getStatusNameById = (id: number) => {
    const statusItem = status_list.find(
      (status) => status.id.toString() === id.toString()
    );
    return statusItem ? statusItem.value : "";
  };

  const [filters, setFilters] = useState<Filters>({
    order_id: searchParams.get("order_id")
      ? searchParams
          .get("order_id")!
          .split(",")
          .map((item) => ({ id: Number(item), name: item } as FilterItem))
      : [],
    owner_id: [],
    reporter_id: [],
    assignee_id: [],
    status: searchParams.get("status")
      ? searchParams
          .get("status")!
          .split(",")
          .map(
            (item) =>
              ({
                id: Number(item),
                name: getStatusNameById(Number(item)),
              } as FilterItem)
          )
      : [],
  });

  const [params, setParams] = useState({
    page_no: 1,
    limit: 30,
    order_id: filters.order_id.map((item) => item.id),
    owner_id: [] as number[],
    reporter_id: [] as FilterItem[],
    assignee_id: [] as FilterItem[],
    status: filters.status.map((item) => item.id),
    sortBy: "created_date",
    sortType: SortType.DESC,
  });

  useEffect(() => {
    const searchText =
      debouncedReporterSearchText || debouncedAssigneeSearchText;
    if (searchText.length > 2) {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedReporterSearchText, debouncedAssigneeSearchText]);

  useEffect(() => {
    if (debouncedOrderIdSearchText.length > 2) {
      TaskTrackerStore.getProjectOrderIds(
        Number(debouncedOrderIdSearchText)
      ).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrderIdSearchText]);

  const updateFilters = (updatedFilters: Partial<Filters>) => {
    reporterSearchRef.current = "";
    assigneeSearchRef.current = "";
    orderIdSearchRef.current = "";
    setFilters((prev) => ({
      ...prev,
      ...updatedFilters,
    }));
  };

  const parseFilterItems = (param: string): FilterItem[] => {
    return param
      ? param.split(",").map((item) => {
          const [id, name] = item.split(":");
          return { id: Number(id), name };
        })
      : [];
  };

  useEffect(() => {
    const orderIdFromUrl = searchParams.get("order_id");
    const reporterIdFromUrl = searchParams.get("reporter_id");
    const assigneeIdFromUrl = searchParams.get("assignee_id");

    setFilters((prev) => ({
      ...prev,
      order_id: orderIdFromUrl
        ? orderIdFromUrl.split(",").map((id) => ({ id: Number(id), name: id }))
        : [],
      reporter_id: reporterIdFromUrl ? parseFilterItems(reporterIdFromUrl) : [],
      assignee_id: assigneeIdFromUrl ? parseFilterItems(assigneeIdFromUrl) : [],
      status: searchParams.get("status")
        ? searchParams
            .get("status")!
            .split(",")
            .map((id) => ({
              id: Number(id),
              name: getStatusNameById(Number(id)),
            }))
        : [],
    }));

    setParams((prev) => ({
      ...prev,
      order_id: orderIdFromUrl ? orderIdFromUrl.split(",").map(Number) : [],
      reporter_id: reporterIdFromUrl ? parseFilterItems(reporterIdFromUrl) : [],
      assignee_id: assigneeIdFromUrl ? parseFilterItems(assigneeIdFromUrl) : [],
      status: searchParams.get("status")?.split(",").map(Number) || [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          const paramValue = Array.isArray(value)
            ? value
                .map((item) =>
                  (key === "reporter_id" || key === "assignee_id") &&
                  typeof item === "object" &&
                  "id" in item &&
                  "name" in item
                    ? `${item.id}:${item.name}`
                    : item.toString()
                )
                .join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      navigate({ search: searchParams.toString() }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const hasAnyUrlParams = searchParams.toString().length > 0;
    if (userStore.user?.id && params.assignee_id.length === 0 && !hasAnyUrlParams) {
      const loggedInAdmin = {
        id: userStore.user.id,
        name: userStore.user.name,
      };
      setFilters((prev) => ({
        ...prev,
        assignee_id: [loggedInAdmin],
      }));
      setParams((prev) => ({
        ...prev,
        assignee_id: [loggedInAdmin],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.user?.id]);

  const handleFilterApply = useCallback(() => {
    setParams((prev) => ({
      ...prev,
      order_id: filters.order_id.map((order) => order.id),
      reporter_id: filters.reporter_id.map((reporter) => ({
        id: reporter.id,
        name: reporter.name,
      })),
      assignee_id: filters.assignee_id.map((assignee) => ({
        id: assignee.id,
        name: assignee.name,
      })),
      status: filters.status.map((status) => status.id),
      page_no: 1,
    }));
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      order_id: [],
      owner_id: [],
      reporter_id: [],
      assignee_id: [],
      status: [],
    });
    setParams({
      page_no: 1,
      limit: 30,
      order_id: [],
      owner_id: [],
      reporter_id: [],
      assignee_id: [],
      status: [],
      sortBy: "due_date",
      sortType: SortType.DESC,
    });
    reporterSearchRef.current = "";
    assigneeSearchRef.current = "";
    orderIdSearchRef.current = "";
    setSearchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const hasAnyUrlParams = new URLSearchParams(location.search).toString().length > 0;
    const shouldSetDefaultAssignee =
      userStore.user?.id &&
      params.assignee_id.length === 0 &&
      !hasAnyUrlParams;
    if (shouldSetDefaultAssignee) {
      return;
    }

    setLoading(true);
    const transformedParams = {
      ...params,
      reporter_id: params.reporter_id.map(
        (reporter: FilterItem) => reporter.id
      ),
      assignee_id: params.assignee_id.map(
        (assignee: FilterItem) => assignee.id
      ),
    };

    TaskTrackerStore.getTasks(transformedParams)
      .then(() => setLoading(false))
      .catch((err) => {
        setLoading(false);
        showError("Error", err?.response?.data?.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleAdminSearch = (type: "reporter" | "assignee", name: string) => {
    if (type === "reporter") {
      reporterSearchRef.current = name;
    } else {
      assigneeSearchRef.current = name;
    }
    setSearchTrigger((prev) => prev + 1);
  };

  const handleOrderIdSearch = (orderId: string) => {
    orderIdSearchRef.current = orderId;
    setSearchTrigger((prev) => prev + 1);
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleSortChange = (newSortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy: newSortBy,
      sortType:
        prevParams.sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
    }));
  };

  const toggleSidebar = (taskId: number) => {
    // setTaskId(taskId);
    if (chatStore.currentTaskId !== taskId) {
      chatStore.page = 1;
      chatStore.setTaskId(taskId);
      setSidebarOpen(true);
    } else {
      chatStore.setTaskId(0);
      setSidebarOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    chatStore.resetMessages();
  }, [chatStore.currentTaskId, chatStore]);

  return (
    <>
      {chatStore.currentTaskId !== 0 && (
        <ChatSidebar
          isOpen={isSidebarOpen}
          width={
            isSidebarOpen
              ? { xs: "90vw", sm: "60vw", md: "30vw", lg: "23vw", xl: "20vw" }
              : "0"
          }
        />
      )}
      <Box sx={{ display: "flex", position: "relative" }}>
        <Box
          sx={{
            position: "fixed",
            top: "4.5rem",
            right: "3px",
            zIndex: 2000,
            transition: "right 0.3s ease",
            borderRadius: "50%",
            cursor: "pointer",
          }}
        >
          <IconButton onClick={() => toggleSidebar(chatStore.currentTaskId)}>
            {isSidebarOpen && (
              <CloseIcon sx={{ color: "black", fontSize: "25px" }} />
            )}
          </IconButton>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            transition: "margin-right 0.3s ease",
            marginRight: isSidebarOpen
              ? {
                  xs: "90vw",
                  sm: "60vw",
                  md: "30.5vw",
                  lg: "23.5vw",
                  xl: "20.5vw",
                }
              : "0",
            padding: "16px",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <PageTitle title="Task Tracker" />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: "16px",
                marginBottom: "24px",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Link to={"/task-tracker/add-edit-task"}>
                  <Button variant="contained" color="primary">
                    Create Task
                  </Button>
                </Link>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: "24px",
                  justifyContent: "end",
                  flexWrap: "wrap",
                }}
              >
                <AppSearchDropDown
                  isMultiSelect
                  title="Select status"
                  textFieldValue=""
                  list={
                    status_list?.map((status) => ({
                      id: status.id.toString(),
                      value: status.value,
                    })) || []
                  }
                  local={false}
                  fontSize="14px"
                  handleSearch={() => {}}
                  minWidth="150px"
                  handleSelect={(selectedStatuses) =>
                    updateFilters({
                      status: selectedStatuses.map((status) => ({
                        id: Number(status.id),
                        name: status.value,
                      })),
                    })
                  }
                  value={filters.status?.map((status) => ({
                    id: status.id.toString(),
                    value: status.name,
                  }))}
                />

                <AppSearchDropDown
                  isMultiSelect
                  title="Select order Id"
                  textFieldValue={orderIdSearchRef.current}
                  list={
                    TaskTrackerStore?.orderIds?.map((orderId) => ({
                      id: orderId.toString(),
                      value: orderId.toString(),
                    })) || []
                  }
                  local={false}
                  fontSize="14px"
                  handleSearch={(orderId) => handleOrderIdSearch(orderId!)}
                  minWidth="150px"
                  handleSelect={(selectedOrders) =>
                    updateFilters({
                      order_id: selectedOrders.map((order) => ({
                        id: Number(order.id),
                        name: order.id,
                      })),
                    })
                  }
                  value={filters.order_id.map((order) => ({
                    id: order.id.toString(),
                    value: order.id.toString(),
                  }))}
                />

                <AppSearchDropDown
                  isMultiSelect
                  title="Select reporter"
                  textFieldValue={reporterSearchRef.current}
                  list={
                    TaskTrackerStore?.admins?.map((admin: Admin) => ({
                      id: admin.id.toString(),
                      value: admin.name,
                    })) || []
                  }
                  local={false}
                  fontSize="14px"
                  handleSearch={(name) => handleAdminSearch("reporter", name!)}
                  minWidth="150px"
                  handleSelect={(selectedReporters) =>
                    updateFilters({
                      reporter_id: selectedReporters.map((reporter) => ({
                        id: Number(reporter.id),
                        name: reporter.value,
                      })),
                    })
                  }
                  value={filters.reporter_id.map((reporter) => ({
                    id: reporter.id.toString(),
                    value: reporter.name,
                  }))}
                />

                <AppSearchDropDown
                  isMultiSelect
                  title="Select assignee"
                  textFieldValue={assigneeSearchRef.current}
                  list={
                    TaskTrackerStore?.admins?.map((admin: Admin) => ({
                      id: admin.id.toString(),
                      value: admin.name,
                    })) || []
                  }
                  local={false}
                  fontSize="14px"
                  handleSearch={(name) => handleAdminSearch("assignee", name!)}
                  minWidth="150px"
                  handleSelect={(selectedAssignees) =>
                    updateFilters({
                      assignee_id: selectedAssignees.map((assignee) => ({
                        id: Number(assignee.id),
                        name: assignee.value,
                      })),
                    })
                  }
                  value={filters.assignee_id.map((assignee) => ({
                    id: assignee.id.toString(),
                    value: assignee.name,
                  }))}
                />

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFilterApply}
                >
                  Apply
                </Button>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <TaskTable
                tasks={TaskTrackerStore.tasks}
                maxHeight={window.innerHeight - 350}
                limit={params.limit || TaskTrackerStore.limit}
                page_no={params.page_no}
                total_count={TaskTrackerStore.total_count}
                total_pages={TaskTrackerStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={handleSortChange}
                sortBy={params.sortBy}
                sortType={params.sortType}
                toggleChat={toggleSidebar}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default observer(TaskTracker);
